'use client';

import { useState } from 'react';
import { useReveal } from '@/hooks/use-reveal';
import { ArrowUpRight } from 'lucide-react';

interface Product {
  id: string;
  name: string;
  category: string;
  description: string;
  price: string;
  image: string;
  detail: string;
}

const PRODUCTS: Product[] = [
  {
    id: 'sac-cabas',
    name: 'Cabas Alma',
    category: 'Sacs à Main',
    description: 'Cabas en cuir pleine fleur bordeaux, anses renforcées au point sellier, douverture en lin.',
    price: 'dès 480 €',
    image: '/images/product-handbag.png',
    detail: 'Cuir taurillon tanné végétal · Patine naturelle',
  },
  {
    id: 'ceinture-artisan',
    name: 'Ceinture Maréchal',
    category: 'Ceintures',
    description: 'Ceinture en cuir mahogany, boucle en laiton massif, couture main cirée.',
    price: 'dès 165 €',
    image: '/images/product-belt.png',
    detail: 'Cuir de vachette pleine fleur · Laiton vieilli',
  },
  {
    id: 'portefeuille-cognac',
    name: 'Portefeuille Auguste',
    category: 'Petite Maroquinerie',
    description: 'Portefeuille en cuir cognac, six emplacements cartes, deux poches billets.',
    price: 'dès 145 €',
    image: '/images/product-wallet.png',
    detail: 'Cuir tanné végétal · Pièce unique numérotée',
  },
  {
    id: 'sac-dos-voyage',
    name: 'Sac à Dos Explorateur',
    category: 'Sacs & Bagagerie',
    description: 'Sac à dos en cuir espresso, sangles réglables, fermoirs en laiton brossé.',
    price: 'dès 690 €',
    image: '/images/product-backpack.png',
    detail: 'Cuir pleine fleur · Garantie à vie',
  },
  {
    id: 'bottes-patinees',
    name: 'Bottines Augusta',
    category: 'Chaussures',
    description: 'Bottines en cuir ambré, montage Goodyear, œillets en laiton poli.',
    price: 'dès 540 €',
    image: '/images/product-boots.png',
    detail: 'Cuir tanné végétal · Semelle en cuir cousue',
  },
  {
    id: 'collection-essentielle',
    name: 'Collection Essentielle',
    category: 'Coffrets Offerts',
    description: 'Coffret réunissant nos pièces maîtresses : sac, ceinture, portefeuille assortis.',
    price: 'dès 1 280 €',
    image: '/images/collection-banner.png',
    detail: 'Coffret bois et velours · Gravure offerte',
  },
];

export default function Collections() {
  const { ref, visible } = useReveal();
  const [hovered, setHovered] = useState<string | null>(null);

  return (
    <section id="collections" ref={ref} className="relative py-28 md:py-40 bg-[#F8F1E4] overflow-hidden">
      {/* Top section heading */}
      <div className="container mx-auto px-6 relative">
        <div className="max-w-3xl mx-auto text-center mb-20">
          <div className={`reveal ${visible ? 'visible' : ''} mb-6`}>
            <div className="flex items-center justify-center gap-4">
              <div className="w-10 h-px bg-[#8B4513]" />
              <span className="text-[#8B4513] font-serif-display text-sm tracking-[0.4em] uppercase">
                Nos Collections
              </span>
              <div className="w-10 h-px bg-[#8B4513]" />
            </div>
          </div>

          <h2 className={`reveal ${visible ? 'visible' : ''} font-display text-[#2B1810] text-4xl md:text-6xl lg:text-7xl font-medium leading-[1.05] mb-8`}>
            Des Pièces qui
            <br />
            <span className="italic text-luxe-gold">vous Ressemblent</span>
          </h2>

          <p className={`reveal ${visible ? 'visible' : ''} text-[#5C2E03]/85 text-lg md:text-xl font-serif-display font-light leading-relaxed`}>
            Découvrez nos collections de maroquinerie d'exception : sacs à main, ceintures,
            portefeuilles, sacs à dos et chaussures. Chaque création est numérotée, signée
            par l'artisan, et accompagnée d'un certificat d'authenticité.
          </p>
        </div>

        {/* Products grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
          {PRODUCTS.map((product, idx) => (
            <article
              key={product.id}
              className={`product-card reveal ${visible ? 'visible' : ''} group cursor-pointer relative`}
              style={{ transitionDelay: `${idx * 80}ms` }}
              onMouseEnter={() => setHovered(product.id)}
              onMouseLeave={() => setHovered(null)}
            >
              {/* Image container */}
              <div className="relative aspect-square overflow-hidden rounded-sm bg-[#2B1810]">
                <img
                  src={product.image}
                  alt={`${product.name} - ${product.category} ASMAO STYLE CUIR`}
                  className="product-image w-full h-full object-cover opacity-95 group-hover:opacity-100"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2B1810]/80 via-transparent to-transparent" />

                {/* Price badge */}
                <div className="absolute top-4 right-4 bg-[#F8F1E4]/95 backdrop-blur-sm px-3 py-1.5 text-[#2B1810] text-xs font-medium tracking-[0.15em] uppercase font-serif-display">
                  {product.price}
                </div>

                {/* Hover detail */}
                <div
                  className={`absolute bottom-4 left-4 right-4 transition-all duration-500 ${
                    hovered === product.id ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
                  }`}
                >
                  <div className="text-[#C9A961] text-[10px] tracking-[0.3em] uppercase font-serif-display mb-1">
                    {product.detail}
                  </div>
                </div>

                {/* Corner ornament */}
                <div className="absolute top-4 left-4 w-6 h-6 border-l border-t border-[#C9A961]/0 group-hover:border-[#C9A961]/80 transition-all duration-500" />
              </div>

              {/* Text content */}
              <div className="pt-5">
                <div className="text-[#8B4513] text-[11px] tracking-[0.3em] uppercase font-serif-display mb-2">
                  {product.category}
                </div>
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-display text-2xl text-[#2B1810] font-medium leading-tight group-hover:text-[#8B4513] transition-colors duration-300">
                    {product.name}
                  </h3>
                  <ArrowUpRight
                    className="text-[#8B4513] shrink-0 mt-1 transition-transform duration-500 group-hover:rotate-45"
                    size={20}
                  />
                </div>
                <p className="mt-3 text-[#5C2E03]/75 text-sm font-serif-display font-light leading-relaxed">
                  {product.description}
                </p>
              </div>
            </article>
          ))}
        </div>

        {/* CTA bottom */}
        <div className={`reveal ${visible ? 'visible' : ''} mt-20 text-center`}>
          <p className="text-[#5C2E03]/70 font-serif-display italic text-lg mb-6">
            Chaque pièce peut être personnalisée : initiales, choix du cuir, couleur de la couture.
          </p>
          <button
            onClick={() => {
              const el = document.querySelector('#contact');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            className="group inline-flex items-center gap-3 px-10 py-4 border border-[#8B4513] text-[#8B4513] hover:bg-[#8B4513] hover:text-[#F8F1E4] font-medium text-sm tracking-[0.25em] uppercase transition-all duration-500 rounded-sm"
          >
            Demander une Création Sur-Mesure
            <ArrowUpRight className="group-hover:rotate-45 transition-transform duration-500" size={18} />
          </button>
        </div>
      </div>
    </section>
  );
}
